import React, { CSSProperties, ReactNode, useRef } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@column/column-ui-kit';
import { Input, Textarea, cn } from '@column/ui';

import { TRANSITION_CLOSE, TRANSITION_OPEN } from './constants';
import { inertWhenHidden } from './inert';

const CardCopyRow: React.FC<{ row: SelectedCardRow }> = ({ row }) => {
  const className = cn(
    '[&_:is(input,textarea)]:bg-transparent [&_:is(input,textarea)]:text-muted-foreground [&_:is(input,textarea)]:shadow-none [&_:is(input,textarea)]:[--input-border-color:transparent]',
    'hover:[&_:is(input,textarea)]:bg-white hover:[&_:is(input,textarea)]:shadow-[inset_0_1px_1px_rgb(0_0_0/0.075),inset_0_2px_4px_rgb(0_0_0/0.025)] hover:[&_:is(input,textarea)]:[--input-border-color:rgb(43_49_67/0.125)] dark:hover:[&_:is(input,textarea)]:bg-input/30',
    '[&_[aria-label=Copy]]:opacity-0 [&_[aria-label=Copy]]:transition-opacity focus-within:[&_[aria-label=Copy]]:opacity-100 hover:[&_[aria-label=Copy]]:opacity-100',
    row.monospaced && '[&_:is(input,textarea)]:font-mono',
    row.emphasized && '[&_:is(input,textarea)]:font-medium [&_:is(input,textarea)]:text-foreground',
    // The field is transparent here, so its copy button fades into the card instead.
    'dark:[--input-copy-fade:var(--color-gray-800)]'
  );

  return (
    <div className="@container/copy-row border-t border-gray-150 dark:border-white/[0.08]">
      <div className="grid items-start leading-4 @tablet/copy-row:grid-cols-[minmax(auto,220px)_auto] @tablet/copy-row:gap-4">
        <div className="px-4 pt-4 text-sm leading-4 font-medium whitespace-nowrap text-gray-600 @tablet/copy-row:p-4 dark:text-gray-300">
          {row.label}
        </div>
        {/* 4px plus the field's own 12px puts the value 16px into its column, like the label in its own. */}
        <div className="px-1 pt-1 pb-2 @tablet/copy-row:py-2 @tablet/copy-row:pr-2">
          {row.multiline ? (
            <Textarea
              variant="copy"
              readOnly
              value={row.value}
              rows={row.value.split('\n').length}
              showCopyButton
              // Sized like the sm Input on the other rows, with its first line level with the label.
              className={cn(
                className,
                '[&_textarea]:min-h-8 [&_textarea]:resize-none [&_textarea]:px-3 [&_textarea]:py-1.5'
              )}
            />
          ) : (
            <Input variant="copy" inputSize="sm" readOnly value={row.value} showCopyButton className={className} />
          )}
        </div>
      </div>
    </div>
  );
};

export interface SelectedCardRow {
  label: string;
  value: string;
  monospaced?: boolean;
  emphasized?: boolean;
  /** One line per `\n`, e.g. an address. */
  multiline?: boolean;
}

export interface SelectedCardProps {
  open: boolean;
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  /** Shown as-is in place of the boxed `icon`, e.g. a round flag. */
  media?: ReactNode;
  rows: SelectedCardRow[];
  onClear?: () => void;
  /** Shows an arrow that opens the selected object, e.g. its detail page. */
  onOpen?: () => void;
  openLabel?: string;
  hasError?: boolean;
  isLoading?: boolean;
  alert?: ReactNode;
  alertTone?: 'error' | 'warning';
  /** Links the title to this page; a chevron appears on hover, like the linked cards elsewhere. */
  titleHref?: string;
  /** Extra controls shown before the clear button, e.g. an Edit button. */
  actions?: ReactNode;
  className?: string;
}

// While collapsing, keeps rendering the last open content so it fades out with the card instead of emptying.
export const SelectedCard: React.FC<SelectedCardProps> = ({
  open,
  title,
  subtitle,
  icon,
  media,
  rows,
  onClear,
  onOpen,
  openLabel = 'Open',
  hasError,
  isLoading,
  alert,
  alertTone = 'error',
  titleHref,
  actions,
  className,
}) => {
  const snapshot = useRef({ title, subtitle, icon, media, rows, hasError, isLoading, alert, alertTone, titleHref });
  if (open) {
    snapshot.current = { title, subtitle, icon, media, rows, hasError, isLoading, alert, alertTone, titleHref };
  }
  const view = open
    ? { title, subtitle, icon, media, rows, hasError, isLoading, alert, alertTone, titleHref }
    : snapshot.current;
  const isWarning = !view.hasError && Boolean(view.alert) && view.alertTone === 'warning';

  return (
    <div
      aria-hidden={!open}
      {...inertWhenHidden(!open)}
      className={cn(
        'grid grid-cols-1 overflow-hidden rounded-lg bg-card text-card-foreground shadow-surface-raised',
        'dark:bg-gray-800 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08)]',
        'transition-all motion-reduce:transition-none',
        open ? TRANSITION_OPEN : TRANSITION_CLOSE,
        open ? 'grid-rows-[1fr] opacity-100' : 'pointer-events-none grid-rows-[0fr] opacity-0',
        (view.hasError || view.alert) &&
          !isWarning &&
          'shadow-[0_0_0_1px_var(--color-destructive)] ring-[3px] ring-destructive/20 dark:shadow-[0_0_0_1px_var(--color-destructive)] dark:ring-destructive/40',
        isWarning &&
          'shadow-[0_0_0_1px_var(--color-orange-400)] ring-[3px] ring-orange-400/20 dark:shadow-[0_0_0_1px_var(--color-orange-500)] dark:ring-orange-500/30',
        className
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {!view.isLoading && view.media}
            {!view.isLoading && !view.media && view.icon && (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-400 shadow-[0_0_0_1px_var(--color-gray-150)] dark:bg-white/5 dark:shadow-[0_0_0_1px_rgb(255_255_255/0.1)]">
                {view.icon}
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              {view.titleHref && !view.isLoading ? (
                <Link
                  to={view.titleHref}
                  className="group/title flex max-w-full items-center gap-1 self-start rounded-sm text-sm font-medium text-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                >
                  <span className="truncate">{view.title}</span>
                  <Icon.ChevronRight
                    style={{ '--icon-size': '16px' } as CSSProperties}
                    className="shrink-0 -translate-x-1 text-muted-foreground opacity-0 transition-[opacity,transform] duration-200 group-hover/title:translate-x-0 group-hover/title:opacity-100 group-focus-visible/title:translate-x-0 group-focus-visible/title:opacity-100 group-active/title:translate-x-0.5 motion-reduce:transition-none"
                  />
                </Link>
              ) : (
                <span className="truncate text-sm font-medium text-foreground">
                  {view.isLoading ? 'Loading…' : view.title}
                </span>
              )}
              {!view.isLoading && view.subtitle && (
                <span className="truncate text-xs text-muted-foreground">{view.subtitle}</span>
              )}
            </div>
          </div>
          {actions}
          {onOpen && (
            <button
              type="button"
              onClick={onOpen}
              aria-label={openLabel}
              data-cy="selected-card-open"
              className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
            >
              <Icon.ArrowUpRight className="[--icon-size:16px]" />
            </button>
          )}
          {onClear && (
            <button
              type="button"
              onClick={onClear}
              aria-label="Clear selection"
              className="-mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
            >
              <Icon.Cross className="[--icon-size:16px]" />
            </button>
          )}
        </div>

        {!view.isLoading && view.rows.map((row) => <CardCopyRow key={row.label} row={row} />)}

        {view.alert && (
          <div
            className={cn(
              'flex items-start gap-2 border-t border-gray-100 px-4 py-2.5 text-xs font-medium dark:border-white/[0.08]',
              view.alertTone === 'warning'
                ? 'bg-orange-500/5 text-orange-600 dark:text-orange-300'
                : 'bg-destructive/5 text-destructive'
            )}
          >
            <Icon.Warning className="mt-px shrink-0 [--icon-size:14px]" />
            <div className="min-w-0 text-pretty whitespace-pre-line">{view.alert}</div>
          </div>
        )}
      </div>
    </div>
  );
};
